import { Field, FieldCheckbox, FieldInput, FieldLabel, FieldSelect } from '@ibe/ui';
import { type ReactNode, useEffect } from 'react';
import {
  Controller,
  useWatch,
  type Control,
  type FieldErrors,
  type UseFormRegister,
  type UseFormSetValue,
} from 'react-hook-form';
import {
  CHANNEL,
  CHANNEL_OPTIONS,
  EMAIL_PROVIDER_OPTIONS,
  PROVIDER,
  SMS_PROVIDER_OPTIONS,
  SMS_TYPE_OPTIONS,
  type GatewayFormInput,
} from '../api/schemas.js';

export interface NotificationGatewayFieldsProps {
  readonly register: UseFormRegister<GatewayFormInput>;
  readonly errors: FieldErrors<GatewayFormInput>;
  readonly control: Control<GatewayFormInput>;
  readonly setValue: UseFormSetValue<GatewayFormInput>;
  readonly mode: 'create' | 'edit';
}

function Section({
  title,
  description,
  children,
}: {
  readonly title: string;
  readonly description?: string;
  readonly children: ReactNode;
}): React.JSX.Element {
  return (
    <fieldset className="space-y-4 border-t border-border pt-4 first:border-t-0 first:pt-0">
      <legend className="sr-only">{title}</legend>
      <div className="space-y-1">
        <h2 className="text-body font-semibold">{title}</h2>
        {description === undefined ? null : (
          <p className="text-caption text-muted-foreground">{description}</p>
        )}
      </div>
      {children}
    </fieldset>
  );
}

function Row({ children }: { readonly children: ReactNode }): React.JSX.Element {
  return <div className="grid grid-cols-1 gap-4 md:grid-cols-2">{children}</div>;
}

function SmtpCredentials({
  register,
  errors,
  control,
}: {
  readonly register: UseFormRegister<GatewayFormInput>;
  readonly errors: FieldErrors<GatewayFormInput>;
  readonly control: Control<GatewayFormInput>;
}): React.JSX.Element {
  return (
    <>
      <Row>
        <Field error={errors.smtpHost?.message} required>
          <FieldLabel>Host</FieldLabel>
          <FieldInput
            autoComplete="off"
            placeholder="smtp.sendgrid.net"
            {...register('smtpHost')}
          />
        </Field>
        <Field error={errors.smtpPort?.message} required>
          <FieldLabel>Port</FieldLabel>
          <FieldInput
            autoComplete="off"
            inputMode="numeric"
            placeholder="587"
            {...register('smtpPort')}
          />
        </Field>
      </Row>
      <Row>
        <Field error={errors.smtpUsername?.message}>
          <FieldLabel>Username</FieldLabel>
          <FieldInput autoComplete="off" {...register('smtpUsername')} />
        </Field>
        <Field error={errors.smtpPassword?.message}>
          <FieldLabel>Password</FieldLabel>
          <FieldInput autoComplete="off" type="password" {...register('smtpPassword')} />
        </Field>
      </Row>
      <Field>
        <Controller
          control={control}
          name="smtpSecure"
          render={({ field }) => (
            <FieldCheckbox
              checked={field.value}
              onCheckedChange={field.onChange}
              onBlur={field.onBlur}
            >
              Use TLS/SSL on connection
            </FieldCheckbox>
          )}
        />
      </Field>
    </>
  );
}

function SendgridCredentials({
  register,
  errors,
}: {
  readonly register: UseFormRegister<GatewayFormInput>;
  readonly errors: FieldErrors<GatewayFormInput>;
}): React.JSX.Element {
  return (
    <Row>
      <Field error={errors.sendgridApiKey?.message} required>
        <FieldLabel>API key</FieldLabel>
        <FieldInput autoComplete="off" type="password" {...register('sendgridApiKey')} />
      </Field>
    </Row>
  );
}

function SesCredentials({
  register,
  errors,
}: {
  readonly register: UseFormRegister<GatewayFormInput>;
  readonly errors: FieldErrors<GatewayFormInput>;
}): React.JSX.Element {
  return (
    <>
      <Row>
        <Field error={errors.sesRegion?.message} required>
          <FieldLabel>AWS region</FieldLabel>
          <FieldInput autoComplete="off" placeholder="ap-southeast-2" {...register('sesRegion')} />
        </Field>
      </Row>
      <Row>
        <Field error={errors.sesAccessKeyId?.message} required>
          <FieldLabel>Access key ID</FieldLabel>
          <FieldInput autoComplete="off" {...register('sesAccessKeyId')} />
        </Field>
        <Field error={errors.sesSecretAccessKey?.message} required>
          <FieldLabel>Secret access key</FieldLabel>
          <FieldInput autoComplete="off" type="password" {...register('sesSecretAccessKey')} />
        </Field>
      </Row>
    </>
  );
}

function TwilioCredentials({
  register,
  errors,
}: {
  readonly register: UseFormRegister<GatewayFormInput>;
  readonly errors: FieldErrors<GatewayFormInput>;
}): React.JSX.Element {
  return (
    <>
      <Row>
        <Field error={errors.twilioAccountSid?.message} required>
          <FieldLabel>Account SID</FieldLabel>
          <FieldInput autoComplete="off" {...register('twilioAccountSid')} />
        </Field>
        <Field error={errors.twilioAuthToken?.message} required>
          <FieldLabel>Auth token</FieldLabel>
          <FieldInput autoComplete="off" type="password" {...register('twilioAuthToken')} />
        </Field>
      </Row>
      <Row>
        <Field
          error={errors.twilioFromNumber?.message}
          description="The Twilio number or sender ID."
        >
          <FieldLabel>From number</FieldLabel>
          <FieldInput autoComplete="off" {...register('twilioFromNumber')} />
        </Field>
      </Row>
    </>
  );
}

function VonageCredentials({
  register,
  errors,
}: {
  readonly register: UseFormRegister<GatewayFormInput>;
  readonly errors: FieldErrors<GatewayFormInput>;
}): React.JSX.Element {
  return (
    <Row>
      <Field error={errors.vonageApiKey?.message} required>
        <FieldLabel>API key</FieldLabel>
        <FieldInput autoComplete="off" {...register('vonageApiKey')} />
      </Field>
      <Field error={errors.vonageApiSecret?.message} required>
        <FieldLabel>API secret</FieldLabel>
        <FieldInput autoComplete="off" type="password" {...register('vonageApiSecret')} />
      </Field>
    </Row>
  );
}

function SnsCredentials({
  register,
  errors,
  control,
}: {
  readonly register: UseFormRegister<GatewayFormInput>;
  readonly errors: FieldErrors<GatewayFormInput>;
  readonly control: Control<GatewayFormInput>;
}): React.JSX.Element {
  return (
    <>
      <Row>
        <Field error={errors.snsSenderId?.message} required>
          <FieldLabel>Sender ID</FieldLabel>
          <FieldInput autoComplete="off" placeholder="e.g. PROPETER" {...register('snsSenderId')} />
        </Field>
        <Field error={errors.snsSmsType?.message} required>
          <FieldLabel>SMS type</FieldLabel>
          <Controller
            control={control}
            name="snsSmsType"
            render={({ field }) => (
              <FieldSelect
                value={field.value}
                onValueChange={field.onChange}
                onBlur={field.onBlur}
                options={SMS_TYPE_OPTIONS}
                placeholder="Select SMS type…"
                searchable
              />
            )}
          />
        </Field>
      </Row>
      <Row>
        <Field error={errors.snsRegion?.message} required>
          <FieldLabel>AWS region</FieldLabel>
          <FieldInput autoComplete="off" placeholder="ap-south-1" {...register('snsRegion')} />
        </Field>
        <Field
          error={errors.snsWebhookSecret?.message}
          description="Used to validate delivery webhooks."
        >
          <FieldLabel>Webhook secret (optional)</FieldLabel>
          <FieldInput autoComplete="off" type="password" {...register('snsWebhookSecret')} />
        </Field>
      </Row>
      <Row>
        <Field error={errors.snsAccessKeyId?.message} required>
          <FieldLabel>Access key ID</FieldLabel>
          <FieldInput autoComplete="off" {...register('snsAccessKeyId')} />
        </Field>
        <Field error={errors.snsSecretAccessKey?.message} required>
          <FieldLabel>Secret access key</FieldLabel>
          <FieldInput autoComplete="off" type="password" {...register('snsSecretAccessKey')} />
        </Field>
      </Row>
    </>
  );
}

function InfobipCredentials({
  register,
  errors,
}: {
  readonly register: UseFormRegister<GatewayFormInput>;
  readonly errors: FieldErrors<GatewayFormInput>;
}): React.JSX.Element {
  return (
    <>
      <Row>
        <Field
          error={errors.infobipSenderId?.message}
          description="The sender name or number shown to recipients."
        >
          <FieldLabel>Sender ID</FieldLabel>
          <FieldInput
            autoComplete="off"
            placeholder="e.g. PROPETER"
            {...register('infobipSenderId')}
          />
        </Field>
        <Field
          error={errors.infobipWebhookSecret?.message}
          description="Used to validate delivery webhooks."
        >
          <FieldLabel>Webhook secret (optional)</FieldLabel>
          <FieldInput autoComplete="off" type="password" {...register('infobipWebhookSecret')} />
        </Field>
      </Row>
      <Row>
        <Field error={errors.infobipApiBaseUrl?.message} required>
          <FieldLabel>API base URL</FieldLabel>
          <FieldInput
            autoComplete="off"
            placeholder="https://api.infobip.com"
            {...register('infobipApiBaseUrl')}
          />
        </Field>
        <Field error={errors.infobipApiKey?.message} required>
          <FieldLabel>API key / app token</FieldLabel>
          <FieldInput autoComplete="off" type="password" {...register('infobipApiKey')} />
        </Field>
      </Row>
    </>
  );
}

export function NotificationGatewayFields({
  register,
  errors,
  control,
  setValue,
  mode,
}: NotificationGatewayFieldsProps): React.JSX.Element {
  const channel = useWatch<GatewayFormInput, 'channel'>({ control, name: 'channel' });
  const provider = useWatch<GatewayFormInput, 'provider'>({ control, name: 'provider' });

  const providerOptions = channel === CHANNEL.EMAIL ? EMAIL_PROVIDER_OPTIONS : SMS_PROVIDER_OPTIONS;

  useEffect(() => {
    if (provider !== '' && !providerOptions.some((option) => option.value === provider)) {
      setValue('provider', '');
    }
  }, [channel, provider, providerOptions, setValue]);

  const modeDescription =
    mode === 'edit'
      ? 'Credentials are not returned for security. Leave every credential field blank to keep the existing set, or enter all of them to replace it.'
      : 'Choose the channel and provider, then fill the connection details below.';

  return (
    <div className="space-y-6">
      <Section title="Gateway type" description={modeDescription}>
        <Row>
          <Controller
            control={control}
            name="channel"
            render={({ field }) => (
              <Field error={errors.channel?.message} required>
                <FieldLabel>Channel</FieldLabel>
                <FieldSelect
                  value={field.value}
                  onValueChange={field.onChange}
                  onBlur={field.onBlur}
                  options={CHANNEL_OPTIONS}
                  placeholder="Select channel…"
                  disabled={mode === 'edit'}
                  searchable
                />
              </Field>
            )}
          />
          <Controller
            control={control}
            name="provider"
            render={({ field }) => (
              <Field error={errors.provider?.message} required>
                <FieldLabel>Provider type</FieldLabel>
                <FieldSelect
                  value={field.value}
                  onValueChange={field.onChange}
                  onBlur={field.onBlur}
                  options={providerOptions}
                  placeholder={channel === '' ? 'Choose a channel first' : 'Select provider…'}
                  disabled={channel === '' || mode === 'edit'}
                  searchable
                />
              </Field>
            )}
          />
        </Row>

        <Field error={errors.name?.message} required>
          <FieldLabel>Gateway name</FieldLabel>
          <FieldInput
            autoComplete="off"
            placeholder="Transactional (SendGrid)"
            {...register('name')}
          />
        </Field>
      </Section>

      <Section
        title="Sending details"
        description="How messages from this gateway will appear to recipients."
      >
        {channel === CHANNEL.EMAIL ? (
          <Row>
            <Field
              error={errors.fromAddress?.message}
              description="The address messages are sent from."
            >
              <FieldLabel>From email</FieldLabel>
              <FieldInput
                autoComplete="off"
                type="email"
                placeholder="noreply@brand.com"
                {...register('fromAddress')}
              />
            </Field>
            <Field
              error={errors.fromName?.message}
              description="The display name, such as 'Propeter Reservations'."
            >
              <FieldLabel>From name</FieldLabel>
              <FieldInput autoComplete="off" {...register('fromName')} />
            </Field>
          </Row>
        ) : (
          <Row>
            <Field
              error={errors.fromName?.message}
              description="The default sender name or number for SMS."
            >
              <FieldLabel>From name</FieldLabel>
              <FieldInput autoComplete="off" {...register('fromName')} />
            </Field>
          </Row>
        )}
      </Section>

      <Section
        title="Configuration"
        description="Set the gateway's active state, default status, and priority for failover."
      >
        <Row>
          <Field
            error={errors.priority?.message}
            description="Lower numbers run first when a send needs a fallback."
          >
            <FieldLabel>Priority</FieldLabel>
            <FieldInput autoComplete="off" inputMode="numeric" {...register('priority')} />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field>
              <Controller
                control={control}
                name="isDefault"
                render={({ field }) => (
                  <FieldCheckbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    onBlur={field.onBlur}
                  >
                    Default for this channel
                  </FieldCheckbox>
                )}
              />
            </Field>
            <Field>
              <Controller
                control={control}
                name="isActive"
                render={({ field }) => (
                  <FieldCheckbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    onBlur={field.onBlur}
                  >
                    Active
                  </FieldCheckbox>
                )}
              />
            </Field>
          </div>
        </Row>
      </Section>

      {provider !== '' && (
        <Section
          title="Credentials"
          description={
            mode === 'edit'
              ? 'Stored credentials are not shown. Leave all fields blank to keep them, or enter a complete new set to replace them.'
              : 'Enter the provider-specific connection details. They will be encrypted at rest.'
          }
        >
          {provider === PROVIDER.SMTP && (
            <SmtpCredentials register={register} errors={errors} control={control} />
          )}
          {provider === PROVIDER.SENDGRID && (
            <SendgridCredentials register={register} errors={errors} />
          )}
          {provider === PROVIDER.SES && <SesCredentials register={register} errors={errors} />}
          {provider === PROVIDER.TWILIO && (
            <TwilioCredentials register={register} errors={errors} />
          )}
          {provider === PROVIDER.VONAGE && (
            <VonageCredentials register={register} errors={errors} />
          )}
          {provider === PROVIDER.AWS_SNS && (
            <SnsCredentials register={register} errors={errors} control={control} />
          )}
          {provider === PROVIDER.INFOBIP && (
            <InfobipCredentials register={register} errors={errors} />
          )}
        </Section>
      )}
    </div>
  );
}
